/**
 * ⭐ FAVORITES MANAGEMENT PAGE
 * Admin dashboard for managing user favorites
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  InputAdornment,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import favoritesService, { Favorite, FavoritesFilters, FavoritesStats } from '../../services/favoritesService';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

const FavoritesManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [statistics, setStatistics] = useState<FavoritesStats | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasMore: false,
  });

  // Filters
  const [filters, setFilters] = useState<FavoritesFilters>({
    page: 1,
    limit: 20,
    entityType: undefined,
    userId: '',
    search: '',
  });

  // Load favorites
  const loadFavorites = async () => {
    setLoading(true);
    try {
      const result = await favoritesService.getFavorites(filters);
      if (result.success && result.data) {
        setFavorites(result.data.favorites);
        setPagination(result.data.pagination);
      } else {
        toast.error(result.error || 'Failed to load favorites');
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      const result = await favoritesService.getStats();
      if (result.success && result.data) {
        setStatistics(result.data);
      } else {
        toast.error(result.error || 'Failed to load statistics');
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast.error('Failed to load statistics');
    }
  };

  useEffect(() => {
    loadFavorites();
    loadStatistics();
  }, [filters.page, filters.limit, filters.entityType]);

  // Handle search
  const handleSearch = () => {
    setFilters({ ...filters, page: 1 });
    loadFavorites();
  };

  // Handle page change
  const handlePageChange = (event: unknown, newPage: number) => {
    setFilters({ ...filters, page: newPage + 1 });
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, limit: parseInt(event.target.value, 10), page: 1 });
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  // Get trend chart data
  const getTrendChartData = () => {
    if (!statistics) return null;

    return {
      labels: statistics.dailyTrend.map((d) => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
      datasets: [
        {
          label: 'Vehicles',
          data: statistics.dailyTrend.map((d) => d.vehicleFavorites),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
        },
        {
          label: 'Drivers',
          data: statistics.dailyTrend.map((d) => d.driverFavorites),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
        },
      ],
    };
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          ⭐ Favorites Management
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadFavorites(); loadStatistics(); }}>
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FavoriteIcon color="error" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {(statistics.overall?.totalFavorites || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Favorites
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {(statistics.overall?.uniqueUsers || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unique Users
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CarIcon color="success" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {(statistics.overall?.vehicleFavorites || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Vehicle Favorites
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <PersonIcon color="info" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {(statistics.overall?.driverFavorites || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Driver Favorites
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Last 7 Days
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {(statistics.overall?.last7Days || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Last 30 Days ({(statistics.overall?.growthRate || 0) > 0 ? '+' : ''}{(statistics.overall?.growthRate || 0).toFixed(1)}% growth)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {(statistics.overall?.last30Days || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content */}
      <Paper>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="All Favorites" />
          <Tab label="Top Vehicles" />
          <Tab label="Top Drivers" />
          <Tab label="Top Users" />
          <Tab label="Trends" />
        </Tabs>

        {/* Tab 1: All Favorites */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search by user, vehicle, or driver..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.entityType || 'all'}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        entityType: e.target.value === 'all' ? undefined : (e.target.value as any),
                        page: 1,
                      })
                    }
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="vehicle">Vehicles</MenuItem>
                    <MenuItem value="driver">Drivers</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={filters.userId}
                  onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Button fullWidth variant="contained" onClick={handleSearch} sx={{ height: '56px' }}>
                  Search
                </Button>
              </Grid>
            </Grid>

            {/* Table */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : favorites.length === 0 ? (
              <Alert severity="info">No favorites found</Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Favorited Item</TableCell>
                        <TableCell>Rating</TableCell>
                        <TableCell>Date Added</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {favorites.filter(f => f && f.id).map((favorite) => (
                        <TableRow key={favorite.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {favorite?.userName || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {favorite?.userEmail || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={favorite?.entityType || 'unknown'}
                              color={favorite?.entityType === 'vehicle' ? 'success' : 'info'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {!favorite.entity ? (
                              <Typography variant="caption" color="error.main">
                                Entity no longer exists (deleted)
                              </Typography>
                            ) : (
                              <Stack direction="row" spacing={2} alignItems="center">
                                {favorite.entity?.imageUrl && (
                                  <Avatar src={favorite.entity.imageUrl} variant="rounded" />
                                )}
                                <Box>
                                  {favorite?.entityType === 'vehicle' ? (
                                    <>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {favorite.entity?.name || 'Unknown Vehicle'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {favorite.entity?.dailyRate ? `₦${favorite.entity.dailyRate.toLocaleString()}/day` : 'N/A'}
                                      </Typography>
                                    </>
                                  ) : (
                                    <>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {favorite.entity?.name || 'Unknown Driver'}
                                      </Typography>
                                      {favorite.entity?.phone && (
                                        <Typography variant="caption" color="text.secondary">
                                          {favorite.entity.phone}
                                        </Typography>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            {favorite?.entity?.rating ? (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <StarIcon sx={{ fontSize: 16, color: 'gold' }} />
                                <Typography variant="body2">
                                  {favorite.entity?.rating?.toFixed(1) || 'N/A'} ({favorite.entity?.totalRatings || 0})
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No ratings
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(favorite?.createdAt)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={pagination.page - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={pagination.limit}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </>
            )}
          </Box>
        )}

        {/* Tab 2: Top Vehicles */}
        {activeTab === 1 && statistics && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {(statistics.topVehicles || []).map((vehicle, index) => (
                <Grid item xs={12} md={6} key={vehicle?.vehicleId || index}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="h4" color="text.secondary">
                          #{index + 1}
                        </Typography>
                        {vehicle?.imageUrl && (
                          <CardMedia
                            component="img"
                            sx={{ width: 80, height: 80, borderRadius: 1, objectFit: 'cover' }}
                            image={vehicle.imageUrl}
                            alt={`${vehicle?.make || ''} ${vehicle?.model || ''}`}
                          />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6">
                            {vehicle?.make || 'Unknown'} {vehicle?.model || 'Vehicle'} ({vehicle?.year || 'N/A'})
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Chip
                              icon={<FavoriteIcon />}
                              label={`${vehicle?.favoriteCount || 0} favorites`}
                              color="error"
                              size="small"
                            />
                            {vehicle?.rating && (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <StarIcon sx={{ fontSize: 16, color: 'gold' }} />
                                <Typography variant="body2">{vehicle.rating.toFixed(1)}</Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Tab 3: Top Drivers */}
        {activeTab === 2 && statistics && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {statistics.topDrivers.map((driver, index) => (
                <Grid item xs={12} md={6} key={driver.driverId}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="h4" color="text.secondary">
                          #{index + 1}
                        </Typography>
                        <Avatar src={driver.imageUrl} sx={{ width: 60, height: 60 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6">{driver.name}</Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Chip
                              icon={<FavoriteIcon />}
                              label={`${driver.favoriteCount} favorites`}
                              color="error"
                              size="small"
                            />
                            {driver.rating && (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <StarIcon sx={{ fontSize: 16, color: 'gold' }} />
                                <Typography variant="body2">{driver.rating.toFixed(1)}</Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Tab 4: Top Users */}
        {activeTab === 3 && statistics && (
          <Box sx={{ p: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Total Favorites</TableCell>
                    <TableCell>Vehicle Favorites</TableCell>
                    <TableCell>Driver Favorites</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(statistics.topUsers || []).map((user, index) => (
                    <TableRow key={user?.userId || index}>
                      <TableCell>
                        <Typography variant="h6">#{index + 1}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {user?.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user?.email || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={user?.favoriteCount || 0} color="primary" />
                      </TableCell>
                      <TableCell>
                        <Chip label={user?.vehicleFavorites || 0} color="success" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={user?.driverFavorites || 0} color="info" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 5: Trends */}
        {activeTab === 4 && statistics && (
          <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Daily Favorites Trend (Last 30 Days)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {getTrendChartData() && <Line data={getTrendChartData()!} />}
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default FavoritesManagementPage;
